import React from 'react';
import styled from '@emotion/styled';

const Section = styled.section`
  padding: 96px 24px;
  background: #ffffff;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 64px;
`;

const Title = styled.h2`
  font-family: 'Space Grotesk', sans-serif;
  font-size: clamp(2rem, 4vw, 2.65rem);
  font-weight: 700;
  color: #0f1116;
  margin-bottom: 12px;
  letter-spacing: -0.04em;

  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const Subtitle = styled.p`
  font-family: 'Public Sans', sans-serif;
  font-size: 18px;
  color: #666a72;
  line-height: 1.6;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: #ffffff;
  padding: 28px;
  border-radius: 18px;
  border: 1px solid #e2e0dc;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(15, 17, 22, 0.08);
    border-color: #0f1116;
  }
`;

const IconWrapper = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background: #eeece8;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  color: #7664c6;

  svg {
    width: 24px;
    height: 24px;
  }
`;

const CardTitle = styled.h3`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 24px;
  font-weight: 600;
  color: #0f1116;
  margin-bottom: 12px;
`;

const CardText = styled.p`
  font-family: 'Public Sans', sans-serif;
  font-size: 16px;
  color: #666a72;
  line-height: 1.6;
  margin-bottom: 24px;
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const ListItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
  font-family: 'Public Sans', sans-serif;
  font-size: 14px;
  color: #303238;

  svg {
    color: #7664c6;
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    margin-top: 4px;
  }
`;

const BenefitsDeepDive: React.FC = () => {
    return (
        <Section>
            <Container>
                <Header>
                    <Title>Why businesses choose SmushChat</Title>
                    <Subtitle>
                        Whether you're a solo founder or scaling a team, we have the tools you need to succeed.
                    </Subtitle>
                </Header>

                <Grid>
                    <Card>
                        <IconWrapper>
                            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                        </IconWrapper>
                        <CardTitle>For Freelancers</CardTitle>
                        <CardText>
                            Look professional and capture leads 24/7 without being glued to your screen.
                        </CardText>
                        <List>
                            <ListItem>
                                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                Automate initial client consultations
                            </ListItem>
                            <ListItem>
                                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                Never miss a potential project inquiry
                            </ListItem>
                            <ListItem>
                                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                Instant answers to pricing & service FAQs
                            </ListItem>
                        </List>
                    </Card>

                    <Card>
                        <IconWrapper>
                            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                        </IconWrapper>
                        <CardTitle>For Startups</CardTitle>
                        <CardText>
                            Scale your support without scaling your headcount. Focus on product, not tickets.
                        </CardText>
                        <List>
                            <ListItem>
                                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                Answer recurring questions from approved content
                            </ListItem>
                            <ListItem>
                                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                Train AI on your docs in minutes
                            </ListItem>
                            <ListItem>
                                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                Seamless human handoff when needed
                            </ListItem>
                        </List>
                    </Card>

                    <Card>
                        <IconWrapper>
                            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                        </IconWrapper>
                        <CardTitle>For Agencies</CardTitle>
                        <CardText>
                            Add value to your client websites and create a new recurring revenue stream.
                        </CardText>
                        <List>
                            <ListItem>
                                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                White-label options available
                            </ListItem>
                            <ListItem>
                                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                Manage multiple clients from one dashboard
                            </ListItem>
                            <ListItem>
                                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                Demonstrate clear ROI with analytics
                            </ListItem>
                        </List>
                    </Card>
                </Grid>
            </Container>
        </Section>
    );
};

export default BenefitsDeepDive;
